import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useMobileTheme, loadThemeFromCloud, saveThemeToCloud, readMobileTheme } from '../../hooks/useMobileTheme';
import type { MobileThemeKey } from '../../hooks/useMobileTheme';
import { supabase } from '../../lib/supabase';
import GuestOnboardingModal from '../../pages/home/components/GuestOnboardingModal';
import { isLineOaJoined, markLineOaJoined } from '../../lib/lineOa';

interface UnreadMessage {
  id: string;
  sender_name: string;
  message: string;
  property_title: string | null;
  created_at: string;
}

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState<UnreadMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [navMonthlyRent, setNavMonthlyRent] = useState<number>(0);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [betaBannerVisible, setBetaBannerVisible] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoggedIn, logout } = useAuth();
  const { theme, setTheme } = useMobileTheme();

  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  const handlePublishClick = () => {
    setMobileMenuOpen(false);
    setShowPublishModal(true);
  };

  useEffect(() => {
    if (!user?.id) { setNavMonthlyRent(0); return; }
    const fetchEarnings = async () => {
      const { data, error } = await supabase
        .from('rented_properties')
        .select('monthly_rent, lease_end, is_paused')
        .eq('user_id', user.id);
      if (!error && data) {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const total = data.reduce((sum: number, r: Record<string, unknown>) => {
          if (r.is_paused) return sum;
          if (r.lease_end) {
            const end = new Date((r.lease_end as string) + 'T00:00:00');
            end.setHours(0, 0, 0, 0);
            if (end.getTime() < now.getTime()) return sum;
          }
          return sum + (parseFloat(r.monthly_rent as string) || 0);
        }, 0);
        setNavMonthlyRent(total);
      }
    };
    fetchEarnings();
    const rentedChannel = supabase
      .channel(`navbar-rented-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rented_properties', filter: `user_id=eq.${user.id}` }, () => fetchEarnings())
      .subscribe();
    return () => { supabase.removeChannel(rentedChannel); };
  }, [user?.id]);

  useEffect(() => {
    if (!user) { setUnreadMessages([]); setUnreadCount(0); return; }
    const fetchUnread = async () => {
      const { data, error } = await supabase
        .from('messages')
        .select('id, sender_name, message, property_title, created_at')
        .eq('landlord_id', user.id).eq('is_read', false)
        .order('created_at', { ascending: false }).limit(3);
      if (!error && data) setUnreadMessages(data as UnreadMessage[]);
      const { count } = await supabase
        .from('messages').select('*', { count: 'exact', head: true })
        .eq('landlord_id', user.id).eq('is_read', false);
      setUnreadCount(count ?? 0);
    };
    fetchUnread();
    const channel = supabase.channel(`navbar-unread-${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `landlord_id=eq.${user.id}` }, () => fetchUnread())
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages', filter: `landlord_id=eq.${user.id}` }, () => fetchUnread())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setDropdownOpen(false);
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) setMoreMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setMoreMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    await logout();
    navigate('/');
  };

  const closeMobile = () => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return '剛剛';
    if (mins < 60) return `${mins}分前`;
    if (hours < 24) return `${hours}小時前`;
    if (days < 7) return `${days}天前`;
    return date.toLocaleDateString('zh-TW', { month: 'short', day: 'numeric' });
  };

  const navLinks = [
    { path: '/', label: '首頁', icon: 'ri-home-4-line' },
    { path: '/landlord-dashboard', label: '我的房地產錢包', icon: 'ri-sparkling-2-line', special: true },
    { path: '/regions', label: '找房源', icon: 'ri-search-line' },
    { path: '/publish', label: '發布房源', icon: 'ri-add-circle-line', isPublish: true },
  ];

  const moreLinks = [
    { path: '/no-commission', label: '免仲介說明', icon: 'ri-money-dollar-circle-line' },
    { path: '/ai-features', label: 'AI功能有哪些', icon: 'ri-robot-2-line' },
    { path: '/contact', label: '聯絡我們', icon: 'ri-customer-service-2-line' },
  ];

  const [themeToast, setThemeToast] = useState<{
    visible: boolean;
    theme: MobileThemeKey;
    saving: boolean;
    saved: boolean;
  }>({ visible: false, theme: 'white', saving: false, saved: false });
  const themeToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevThemeRef = useRef<MobileThemeKey | null>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!user?.id) return;
    loadThemeFromCloud(user.id).then((cloudTheme) => {
      if (cloudTheme && cloudTheme !== readMobileTheme()) {
        isFirstRender.current = true;
        setTheme(cloudTheme);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (isFirstRender.current) {
      prevThemeRef.current = theme;
      isFirstRender.current = false;
      return;
    }
    if (prevThemeRef.current === theme) return;
    prevThemeRef.current = theme;
    if (themeToastTimer.current) clearTimeout(themeToastTimer.current);
    setThemeToast({ visible: true, theme, saving: false, saved: false });
    themeToastTimer.current = setTimeout(() => {
      setThemeToast(prev => ({ ...prev, visible: false }));
    }, 5000);
    return () => {
      if (themeToastTimer.current) clearTimeout(themeToastTimer.current);
    };
  }, [theme]);

  const handleSaveTheme = async () => {
    if (!user?.id) return;
    setThemeToast(prev => ({ ...prev, saving: true }));
    const ok = await saveThemeToCloud(user.id, theme);
    setThemeToast(prev => ({ ...prev, saving: false, saved: ok }));
    if (themeToastTimer.current) clearTimeout(themeToastTimer.current);
    themeToastTimer.current = setTimeout(() => {
      setThemeToast(prev => ({ ...prev, visible: false }));
    }, 2500);
  };

  const THEME_DOTS: Record<MobileThemeKey, { color: string; label: string; desc: string }> = {
    tech:  { color: '#2dd4bf', label: '科技', desc: '科技深色系' },
    pink:  { color: '#fb7185', label: '粉紅', desc: '粉紅少女系' },
    white: { color: '#d1d5db', label: '白色', desc: '白色簡約系' },
  };
  const THEME_ORDER: MobileThemeKey[] = ['tech', 'pink', 'white'];

  const [lineOaJoined, setLineOaJoined] = useState(() => isLineOaJoined());

  useEffect(() => {
    if (lineOaJoined) return;
    const checkLineAuth = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;
      const identities = authUser.identities ?? [];
      const hasLine = identities.some((id: { provider: string }) => id.provider === 'line');
      if (hasLine) {
        markLineOaJoined();
        setLineOaJoined(true);
      }
    };
    checkLineAuth();
  }, [lineOaJoined]);

  const handleLineOaJoin = () => {
    markLineOaJoined();
    setLineOaJoined(true);
  };

  return (
    <nav id="main-nav" className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
      isScrolled || mobileMenuOpen ? 'bg-white shadow-md' : 'bg-transparent'
    }`}>
      {/* ── Beta 公告條 ── */}
      {betaBannerVisible && (
        <div className="relative z-10 flex items-center justify-center gap-2 px-8 py-1.5 text-xs font-medium" style={{ background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 40%, #134e4a 70%, #0f172a 100%)', color: '#fcd34d' }}>
          <i className="ri-flask-line text-sm flex-shrink-0" style={{ color: '#fcd34d' }}></i>
          <span className="md:hidden whitespace-nowrap" style={{ color: '#fef3c7' }}>開放測試版本 · 目前功能皆可正常使用</span>
          <span className="hidden md:inline" style={{ color: '#fef3c7' }}>目前為開放測試版本 · 功能持續優化中 · 目前功能皆可正常使用，感謝您的支持與回饋！</span>
          <button
            type="button"
            onClick={() => setBetaBannerVisible(false)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center transition-colors cursor-pointer"
            style={{ color: 'rgba(253,224,71,0.5)' }}
            onMouseEnter={e => (e.currentTarget.style.color = '#fcd34d')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(253,224,71,0.5)')}
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}

      {!isScrolled && !mobileMenuOpen && (
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-transparent pointer-events-none" />
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 relative">
        <div className="flex items-center justify-between gap-2 min-w-0">

          {/* Logo */}
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 flex-shrink-0 min-w-0"
            style={{ touchAction: 'manipulation' }}
          >
            <img
              src="https://public.readdy.ai/ai/img_res/d72a3498-c157-4cc6-8081-f37fa1e12754.png"
              alt="藍舍AI地產" className="h-9 w-9 object-contain flex-shrink-0" loading="lazy"
            />
            <span className="flex items-baseline gap-0.5 whitespace-nowrap flex-shrink-0">
              <span className={`text-base md:text-lg font-black tracking-tight transition-colors ${isScrolled || mobileMenuOpen ? 'text-gray-900' : 'text-white drop-shadow-md'}`}>藍舍</span>
              <span className={`text-base md:text-lg font-black tracking-tight transition-colors ${isScrolled || mobileMenuOpen ? 'text-teal-500' : 'text-teal-300 drop-shadow-md'}`}>AI</span>
              <span className={`text-base md:text-lg font-black tracking-tight transition-colors ${isScrolled || mobileMenuOpen ? 'text-gray-900' : 'text-white drop-shadow-md'}`}>地產</span>
            </span>
          </Link>

          {/* ──── 桌面導覽 ──── */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              link.special ? (
                <Link key={link.path} to={link.path}
                  className="relative flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap cursor-pointer group"
                >
                  <span className={`absolute inset-0 rounded-full blur-sm opacity-60 group-hover:opacity-90 transition-opacity ${isScrolled ? 'bg-teal-400/40' : 'bg-teal-300/30'}`}></span>
                  <span className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors ${
                    isScrolled ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white border-teal-400 shadow-md shadow-teal-200'
                    : 'bg-white/20 text-white border-white/40 shadow-lg'
                  }`}>
                    <i className="ri-sparkling-2-fill text-xs animate-pulse"></i>
                    {link.label}
                    {isLoggedIn && navMonthlyRent > 0 && (
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap ${isScrolled ? 'bg-white/25 text-white' : 'bg-white/20 text-white'}`}>
                        ${navMonthlyRent.toLocaleString('zh-TW', { maximumFractionDigits: 0 })}/月
                      </span>
                    )}
                    {(!isLoggedIn || navMonthlyRent === 0) && (
                      <span className={`text-[9px] font-bold px-1 py-0.5 rounded-full leading-none ${isScrolled ? 'bg-yellow-400 text-yellow-900' : 'bg-yellow-300 text-yellow-900'}`}>NEW</span>
                    )}
                  </span>
                </Link>
              ) : (link as { isPublish?: boolean }).isPublish ? (
                <button key={link.path} type="button" onClick={handlePublishClick}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer bg-transparent border-0 p-0 ${
                    location.pathname === link.path
                      ? (isScrolled ? 'text-teal-600' : 'text-white drop-shadow-sm')
                      : (isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white drop-shadow-sm hover:text-white/80')
                  }`}
                >
                  {link.label}
                </button>
              ) : (
                <Link key={link.path} to={link.path}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    location.pathname === link.path
                      ? (isScrolled ? 'text-teal-600' : 'text-white drop-shadow-sm')
                      : (isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white drop-shadow-sm hover:text-white/80')
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}

            {/* 更多 下拉 */}
            <div ref={moreMenuRef} className="relative">
              <button onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  moreLinks.some(l => location.pathname === l.path)
                    ? (isScrolled ? 'text-teal-600 font-semibold' : 'text-white font-semibold')
                    : (isScrolled ? 'text-gray-500 hover:text-gray-700' : 'text-white/70 hover:text-white/90')
                }`}
              >
                更多 <i className={`ri-arrow-down-s-line text-sm transition-transform ${moreMenuOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {moreMenuOpen && (
                <div className="absolute left-0 top-9 w-44 bg-white rounded-xl border border-gray-100 py-1.5 z-50" style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.10)' }}>
                  {moreLinks.map(link => (
                    <Link key={link.path} to={link.path} onClick={() => setMoreMenuOpen(false)}
                      className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                        location.pathname === link.path ? 'bg-teal-50 text-teal-600 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <i className={`${link.icon} text-base text-gray-400`}></i>{link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* 主題切換器（桌機） */}
            <div className={`hidden lg:flex items-center gap-1 px-2 py-1.5 rounded-full border backdrop-blur-md transition-colors duration-300 ${
              isScrolled ? 'bg-gray-50 border-gray-200' : theme === 'tech' ? 'bg-black/30 border-white/20' : 'bg-white/60 border-white/60'
            }`}>
              <i className={`ri-palette-line text-xs mr-0.5 ${isScrolled ? 'text-gray-400' : theme === 'tech' ? 'text-white/50' : 'text-gray-500'}`}></i>
              {THEME_ORDER.map((key) => {
                const isActive = theme === key;
                return (
                  <button key={key} type="button" title={THEME_DOTS[key].label} onClick={() => setTheme(key)}
                    style={{ touchAction: 'manipulation' }}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? isScrolled ? 'bg-gray-800 text-white scale-105' : theme === 'tech' ? 'bg-white text-gray-900 scale-105' : 'bg-gray-800 text-white scale-105'
                        : isScrolled ? 'text-gray-500 hover:text-gray-800 hover:bg-gray-100' : theme === 'tech' ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isActive ? 'ring-2 ring-offset-1 ring-current/40 scale-110' : ''}`}
                      style={{ backgroundColor: THEME_DOTS[key].color }}></span>
                    {THEME_DOTS[key].label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ──── 右側操作區 ──── */}
          <div ref={dropdownRef} className="relative flex items-center gap-2 flex-shrink-0">

            {/* 桌機：已登入 */}
            {isLoggedIn && user && (
              <div className="hidden md:flex items-center gap-2">
                <button onClick={() => setDropdownOpen(!dropdownOpen)}
                  style={{ touchAction: 'manipulation' }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isScrolled ? 'bg-teal-50 text-teal-700 hover:bg-teal-100' : 'bg-black/35 text-white hover:bg-black/50 border border-white/20'
                  }`}
                >
                  <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {user.name ? user.name.charAt(0) : user.email.charAt(0)}
                  </div>
                  <span className="max-w-[80px] truncate">{user.name || user.email}</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
              </div>
            )}

            {/* 桌機：未登入 */}
            {!isLoggedIn && (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login"
                  className={`text-sm font-medium px-4 py-1.5 rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white drop-shadow-sm hover:text-white/80'
                  }`}
                >登入</Link>
                <Link to="/register"
                  className={`text-sm font-semibold px-4 py-1.5 rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    isScrolled ? 'bg-teal-500 text-white hover:bg-teal-600' : 'bg-white text-teal-600 hover:bg-teal-50 shadow-md'
                  }`}
                >免費註冊</Link>
              </div>
            )}

            {/* 桌機：Dropdown 面板 */}
            {isLoggedIn && user && dropdownOpen && (
              <div className="hidden md:block absolute right-0 top-12 w-72 bg-white rounded-xl border border-gray-100 py-1 z-50" style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
                <div className="px-4 py-2.5 border-b border-gray-100">
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  <p className="text-xs text-teal-600 font-medium mt-0.5">{user.role === 'landlord' ? '屋主' : '租客'}</p>
                </div>
                <div className="px-3 pt-2 pb-1">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-mail-line text-sm text-gray-500"></i>
                      <span className="text-xs font-semibold text-gray-700">收件夾</span>
                      {unreadCount > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-black rounded-full leading-none">{unreadCount} 則未讀</span>
                      )}
                    </div>
                    <Link to="/account?tab=inbox" onClick={() => setDropdownOpen(false)}
                      className="text-[11px] text-teal-500 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
                    >查看全部 →</Link>
                  </div>
                  {unreadCount === 0 ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 rounded-lg">
                      <i className="ri-checkbox-circle-line text-gray-300 text-base"></i>
                      <span className="text-xs text-gray-400">目前沒有未讀訊息</span>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {unreadMessages.map((msg) => (
                        <Link key={msg.id} to="/account?tab=inbox" onClick={() => setDropdownOpen(false)}
                          className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-teal-50/60 hover:bg-teal-50 transition-colors cursor-pointer border border-teal-100/50"
                        >
                          <div className="w-7 h-7 rounded-full bg-teal-500 flex items-center justify-center text-white flex-shrink-0 mt-0.5">
                            <i className="ri-user-line text-xs"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-1 mb-0.5">
                              <span className="text-xs font-semibold text-gray-800 truncate">{msg.property_title ?? '未知房源'}</span>
                              <span className="text-[10px] text-gray-400 flex-shrink-0">{formatTime(msg.created_at)}</span>
                            </div>
                            <p className="text-[11px] text-gray-500 line-clamp-1">{msg.message}</p>
                          </div>
                        </Link>
                      ))}
                      {unreadCount > 3 && <p className="text-center text-[11px] text-gray-400 py-1">還有 {unreadCount - 3} 則未讀...</p>}
                    </div>
                  )}
                </div>
                <div className="border-t border-gray-100 mt-1"></div>
                <Link to="/landlord-dashboard" onClick={() => setDropdownOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 cursor-pointer">
                  <i className="ri-wallet-3-line text-base text-teal-500"></i>
                  <span className="font-medium text-teal-700">我的房地產錢包</span>
                  <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-teal-500 text-white leading-none">AI</span>
                </Link>
                <Link to="/account" onClick={() => setDropdownOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                  <i className="ri-settings-3-line text-base text-gray-400"></i>帳號設定
                </Link>
                <div className="border-t border-gray-100 mt-1">
                  <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer whitespace-nowrap">
                    <i className="ri-logout-box-r-line text-base"></i>登出
                  </button>
                </div>
              </div>
            )}

            {/* ──── 手機 pill ──── */}
            {!isLoggedIn ? (
              <div className="md:hidden flex items-center gap-1.5 flex-shrink-0">
                <Link to="/login" style={{ touchAction: 'manipulation' }}
                  className={`text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${mobileMenuOpen || isScrolled ? 'text-gray-700' : 'text-white drop-shadow-sm'}`}
                >登入</Link>
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ touchAction: 'manipulation' }}
                  className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${
                    mobileMenuOpen ? 'border-teal-400 bg-teal-500 text-white' : isScrolled ? 'border-gray-200 bg-white text-gray-700' : 'border-white/40 bg-white/15 text-white'
                  }`}
                >
                  <i className={`text-base ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
                </button>
              </div>
            ) : (
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ touchAction: 'manipulation' }}
                className={`md:hidden flex-shrink-0 flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-full border transition-colors cursor-pointer ${
                  mobileMenuOpen ? 'border-teal-400 bg-teal-500 text-white' : isScrolled ? 'border-gray-200 bg-white text-gray-700' : 'border-white/40 bg-white/15 text-white'
                }`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${mobileMenuOpen ? 'bg-white text-teal-600' : 'bg-teal-500 text-white'}`}>
                  {user?.name ? user.name.charAt(0) : user?.email?.charAt(0)}
                </div>
                {unreadCount > 0 && (
                  <span className="ml-auto text-xs font-bold px-2 py-0.5 bg-red-500 text-white rounded-full">{unreadCount}</span>
                )}
                <i className={`text-sm flex-shrink-0 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ──────────────────────────────────────
          手機選單 Panel（重新整理排序）
      ────────────────────────────────────── */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 z-40 max-h-[85vh] overflow-y-auto">

          {/* ── 區塊 1：用戶資料 + LINE 提醒 ── */}
          {isLoggedIn && user && (
            <div className="px-4 pt-4 pb-4 border-b border-gray-100">
              {/* 頭像 + 名稱 + 身份 */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-teal-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  {user.name ? user.name.charAt(0) : user.email.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{user.name || user.email}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-teal-600 font-medium">
                      {user.role === 'landlord' ? '屋主' : '租客'}
                    </span>
                    {lineOaJoined && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold" style={{ color: '#06C755' }}>
                        <i className="ri-checkbox-circle-fill" style={{ fontSize: '12px' }}></i>
                        LINE 已加入
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* LINE 未加入提醒卡片（全寬、顯眼） */}
              {!lineOaJoined && (
                <a
                  href="https://line.me/R/ti/p/@381szuhf"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleLineOaJoin}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-2xl cursor-pointer transition-all active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #06C755 0%, #04a847 100%)' }}
                >
                  <div className="w-10 h-10 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-fill text-white text-xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-bold leading-tight">您目前還沒加入 LINE 通知機器人</p>
                    <p className="text-white/80 text-xs mt-0.5">加入後配對成功可即時收到通知</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                    </span>
                    <i className="ri-arrow-right-s-line text-white text-xl"></i>
                  </div>
                </a>
              )}
            </div>
          )}

          {/* ── 區塊 2：主導覽 ── */}
          <div className="px-3 pt-3 pb-2">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-2 pb-2">導覽</p>
            <div className="space-y-0.5">
              {navLinks.map((link) => (
                link.special ? (
                  <Link key={link.path} to={link.path} onClick={closeMobile}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-teal-500 to-cyan-500 text-white cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${link.icon} text-base animate-pulse`}></i>
                    </div>
                    <span className="flex-1">{link.label}</span>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-yellow-400 text-yellow-900 leading-none whitespace-nowrap">NEW</span>
                  </Link>
                ) : (link as { isPublish?: boolean }).isPublish ? (
                  <button key={link.path} type="button" onClick={handlePublishClick}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors cursor-pointer text-left ${
                      location.pathname === link.path ? 'bg-teal-50 text-teal-600' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${link.icon} text-base text-gray-400`}></i>
                    </div>
                    {link.label}
                  </button>
                ) : (
                  <Link key={link.path} to={link.path} onClick={closeMobile}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                      location.pathname === link.path ? 'bg-teal-50 text-teal-600' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${link.icon} text-base text-gray-400`}></i>
                    </div>
                    {link.label}
                  </Link>
                )
              ))}
            </div>
          </div>

          {/* ── 區塊 3：我的帳號（已登入才顯示） ── */}
          {isLoggedIn && user && (
            <div className="px-3 pt-1 pb-2 border-t border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-2 py-2">我的帳號</p>
              <div className="space-y-0.5">
                <Link to="/account?tab=inbox" onClick={closeMobile}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-mail-line text-base ${unreadCount > 0 ? 'text-teal-500' : 'text-gray-400'}`}></i>
                  </div>
                  <span className="flex-1">收件夾</span>
                  {unreadCount > 0 && (
                    <span className="text-xs font-bold px-2 py-0.5 bg-red-500 text-white rounded-full">{unreadCount}</span>
                  )}
                </Link>
                <Link to="/account" onClick={closeMobile}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-3-line text-base text-gray-400"></i>
                  </div>
                  帳號設定
                </Link>
              </div>
            </div>
          )}

          {/* ── 區塊 4：更多 ── */}
          <div className="px-3 pt-1 pb-2 border-t border-gray-100">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-2 py-2">更多</p>
            <div className="space-y-0.5">
              {moreLinks.map(link => (
                <Link key={link.path} to={link.path} onClick={closeMobile}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                    location.pathname === link.path ? 'bg-teal-50 text-teal-600' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${link.icon} text-base text-gray-400`}></i>
                  </div>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* ── 區塊 5：介面色系 ── */}
          <div className="px-3 pt-1 pb-4 border-t border-gray-100">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-2 py-2">介面色系</p>
            <div className="flex gap-2 px-1">
              {THEME_ORDER.map((key) => {
                const isActive = theme === key;
                return (
                  <button key={key} type="button"
                    onClick={() => { setTheme(key); setMobileMenuOpen(false); }}
                    className={`flex-1 flex flex-col items-center gap-1.5 py-2.5 rounded-xl border-2 transition-all cursor-pointer ${
                      isActive ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white hover:border-gray-400'
                    }`}
                  >
                    <span className="w-5 h-5 rounded-full border-2 border-white"
                      style={{ backgroundColor: THEME_DOTS[key].color, boxShadow: isActive ? '0 0 0 2px #1f2937' : 'none' }}
                    ></span>
                    <span className={`text-xs font-semibold whitespace-nowrap ${isActive ? 'text-white' : 'text-gray-600'}`}>
                      {THEME_DOTS[key].label}
                    </span>
                  </button>
                );
              })}
            </div>
            {isLoggedIn && (
              <p className="text-[10px] text-gray-400 text-center mt-2">切換後點選右下角通知即可儲存</p>
            )}
          </div>

          {/* ── 區塊 6：未登入 → 登入 / 註冊 ── */}
          {!isLoggedIn && (
            <div className="px-4 pb-5 border-t border-gray-100 pt-3 flex gap-2">
              <Link to="/login" onClick={closeMobile}
                className="flex-1 text-center py-2.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >登入</Link>
              <Link to="/register" onClick={closeMobile}
                className="flex-1 text-center py-2.5 text-sm font-semibold text-white bg-teal-500 rounded-xl hover:bg-teal-600 cursor-pointer whitespace-nowrap"
              >免費註冊</Link>
            </div>
          )}

          {/* ── 區塊 7：登出 ── */}
          {isLoggedIn && (
            <div className="px-3 pb-5 border-t border-gray-100 pt-2">
              <button onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-500 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-logout-box-r-line text-base"></i>
                </div>
                登出
              </button>
            </div>
          )}
        </div>
      )}

      {showPublishModal && (
        <GuestOnboardingModal onClose={() => setShowPublishModal(false)} isLoggedIn={isLoggedIn} />
      )}

      {/* ── 主題切換 Toast ── */}
      <div
        className="fixed bottom-6 right-4 z-[9999] pointer-events-none"
        style={{
          transition: 'all 0.35s cubic-bezier(0.34,1.56,0.64,1)',
          transform: themeToast.visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.92)',
          opacity: themeToast.visible ? 1 : 0,
        }}
      >
        <div className="pointer-events-auto flex items-center gap-3 pl-3 pr-2 py-2.5 bg-gray-900/95 text-white rounded-2xl" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.25)', backdropFilter: 'blur(12px)', minWidth: 220 }}>
          <span className="w-4 h-4 rounded-full flex-shrink-0 border-2 border-white/30" style={{ backgroundColor: THEME_DOTS[themeToast.theme].color }}></span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold leading-none">已切換至{THEME_DOTS[themeToast.theme].desc}</p>
            {!isLoggedIn && <p className="text-[10px] text-white/50 mt-0.5 leading-none">登入後可儲存設定</p>}
          </div>
          {isLoggedIn && !themeToast.saved && (
            <button type="button" onClick={handleSaveTheme} disabled={themeToast.saving}
              className="flex items-center gap-1 px-3 py-1.5 bg-teal-500 hover:bg-teal-400 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              {themeToast.saving ? <><i className="ri-loader-4-line animate-spin text-xs"></i>儲存中</> : <><i className="ri-save-3-line text-xs"></i>儲存</>}
            </button>
          )}
          {isLoggedIn && themeToast.saved && (
            <span className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-xs font-bold rounded-xl whitespace-nowrap flex-shrink-0">
              <i className="ri-check-line text-xs"></i>已儲存
            </span>
          )}
          <button type="button" onClick={() => setThemeToast(prev => ({ ...prev, visible: false }))}
            className="w-6 h-6 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      </div>
    </nav>
  );
}
